// QuizDetails.tsx
// Detailed view of a single quiz with moderation controls

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuiz, useQuizResponses, flagQuiz, approveQuiz, deleteQuiz } from '../firebase/useQuizzes';
import { useAuth } from '../contexts/AuthContext';
import './QuizDetails.css';

export const QuizDetails: React.FC = () => {
    const { quizId } = useParams<{ quizId: string }>();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { quiz, loading, error } = useQuiz(quizId || '');
    const { responses, loading: responsesLoading } = useQuizResponses(quizId || '');

    const [showFlagDialog, setShowFlagDialog] = useState(false);
    const [flagReason, setFlagReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const handleFlagQuiz = async () => {
        if (!quiz || !currentUser?.email || !flagReason.trim()) return;

        setActionLoading(true);
        const result = await flagQuiz(quiz.id, flagReason, currentUser.email);

        if (result.success) {
            setActionMessage({ type: 'success', text: 'Quiz flagged successfully' });
            setShowFlagDialog(false);
            setFlagReason('');
            setTimeout(() => window.location.reload(), 1500);
        } else {
            setActionMessage({ type: 'error', text: 'Failed to flag quiz' });
        }
        setActionLoading(false);
    };

    const handleApproveQuiz = async () => {
        if (!quiz || !currentUser?.email) return;

        if (!window.confirm('Are you sure you want to approve this quiz?')) return;

        setActionLoading(true);
        const result = await approveQuiz(quiz.id, currentUser.email);

        if (result.success) {
            setActionMessage({ type: 'success', text: 'Quiz approved successfully' });
            setTimeout(() => window.location.reload(), 1500);
        } else {
            setActionMessage({ type: 'error', text: 'Failed to approve quiz' });
        }
        setActionLoading(false);
    };

    const handleDeleteQuiz = async () => {
        if (!quiz) return;

        const confirmed = window.confirm(
            'Are you sure you want to delete this quiz? This action cannot be undone.'
        );
        if (!confirmed) return;

        setActionLoading(true);
        const result = await deleteQuiz(quiz.id);

        if (result.success) {
            setActionMessage({ type: 'success', text: 'Quiz deleted successfully' });
            setTimeout(() => navigate('/quiz-creator'), 1500);
        } else {
            setActionMessage({ type: 'error', text: 'Failed to delete quiz' });
        }
        setActionLoading(false);
    };

    const handleViewAnalytics = () => {
        navigate(`/quiz-creator/analytics/${quizId}`);
    };

    if (loading) {
        return (
            <div className="quiz-details">
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Loading quiz details...</p>
                </div>
            </div>
        );
    }

    if (error || !quiz) {
        return (
            <div className="quiz-details">
                <div className="error-message">
                    <h3>Error Loading Quiz</h3>
                    <p>{error?.message || 'Quiz not found'}</p>
                    <button onClick={() => navigate('/quiz-creator')}>
                        Back to Quiz Library
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="quiz-details">
            {/* Header */}
            <header className="quiz-details-header">
                <button className="back-button" onClick={() => navigate('/quiz-creator')}>
                    ← Back to Library
                </button>
                <h1>{quiz.title}</h1>
            </header>

            {/* Action Message */}
            {actionMessage && (
                <div className={`action-message ${actionMessage.type}`}>
                    {actionMessage.text}
                </div>
            )}

            {/* Quiz Info Section */}
            <div className="quiz-info-section">
                <div className="quiz-info-card">
                    <div className="info-header">
                        <h2>Quiz Information</h2>
                        <div className="status-badges">
                            {quiz.isDraft && <span className="badge badge-draft">Draft</span>}
                            {quiz.isPublished && <span className="badge badge-published">Published</span>}
                            {quiz.reviewStatus === 'flagged' && (
                                <span className="badge badge-flagged">Flagged</span>
                            )}
                            {quiz.reviewStatus === 'approved' && (
                                <span className="badge badge-approved">Approved</span>
                            )}
                        </div>
                    </div>

                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Description:</span>
                            <span className="info-value">{quiz.description}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Category:</span>
                            <span className="info-value">{quiz.category}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Difficulty:</span>
                            <span className="info-value difficulty" data-difficulty={quiz.difficulty}>
                                {quiz.difficulty}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Questions:</span>
                            <span className="info-value">{quiz.questionCount}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Total Points:</span>
                            <span className="info-value">{quiz.totalPoints}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Created:</span>
                            <span className="info-value">
                                {new Date(quiz.createdAt).toLocaleDateString()}
                            </span>
                        </div>
                        {quiz.timeLimit && (
                            <div className="info-item">
                                <span className="info-label">Time Limit:</span>
                                <span className="info-value">{quiz.timeLimit} minutes</span>
                            </div>
                        )}
                    </div>

                    {quiz.flaggedReason && (
                        <div className="flagged-section">
                            <h4>🚩 Flagged Reason:</h4>
                            <p>{quiz.flaggedReason}</p>
                            {quiz.reviewedBy && (
                                <p className="reviewer-info">
                                    Reviewed by: {quiz.reviewedBy}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Engagement Stats */}
                <div className="engagement-stats">
                    <h3>Engagement Statistics</h3>
                    <div className="stats-grid">
                        <div className="stat-box">
                            <span className="stat-icon">👥</span>
                            <span className="stat-number">{quiz.attemptCount}</span>
                            <span className="stat-label">Attempts</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-icon">✅</span>
                            <span className="stat-number">{quiz.completionCount}</span>
                            <span className="stat-label">Completions</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-icon">📊</span>
                            <span className="stat-number">
                                {quiz.averageScore > 0 ? `${quiz.averageScore.toFixed(1)}%` : 'N/A'}
                            </span>
                            <span className="stat-label">Avg Score</span>
                        </div>
                    </div>
                    <button className="view-analytics-btn" onClick={handleViewAnalytics}>
                        View Full Analytics
                    </button>
                </div>
            </div>

            {/* Questions Section */}
            <div className="questions-section">
                <h2>Questions ({quiz.questions.length})</h2>
                <div className="questions-list">
                    {quiz.questions
                        .sort((a, b) => a.order - b.order)
                        .map((question, index) => (
                            <div key={question.id} className="question-card">
                                <div className="question-header">
                                    <span className="question-number">Question {index + 1}</span>
                                    <span className="question-points">{question.points} pts</span>
                                </div>
                                <p className="question-text">{question.questionText}</p>

                                {question.questionType === 'multiple_choice' && question.options && (
                                    <div className="options-list">
                                        {question.options.map((option, optIndex) => (
                                            <div
                                                key={optIndex}
                                                className={`option ${
                                                    option === question.correctAnswer ? 'correct' : ''
                                                }`}
                                            >
                                                {option}
                                                {option === question.correctAnswer && (
                                                    <span className="correct-indicator">✓ Correct</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {question.questionType === 'true_false' && (
                                    <div className="true-false">
                                        <span className="correct-answer">
                                            Correct Answer: {question.correctAnswer}
                                        </span>
                                    </div>
                                )}

                                {question.questionType === 'short_answer' && (
                                    <div className="short-answer">
                                        <span className="correct-answer">
                                            Expected Answer: {question.correctAnswer}
                                        </span>
                                    </div>
                                )}

                                {question.explanation && (
                                    <div className="explanation">
                                        <strong>Explanation:</strong> {question.explanation}
                                    </div>
                                )}
                            </div>
                        ))}
                </div>
            </div>

            {/* Recent Responses Section */}
            {!responsesLoading && responses.length > 0 && (
                <div className="responses-section">
                    <h2>Recent Responses ({responses.length})</h2>
                    <div className="responses-list">
                        {responses.slice(0, 5).map((response) => (
                            <div key={response.id} className="response-card">
                                <div className="response-header">
                                    <span className="response-user">
                                        {response.responderUsername || 'Anonymous'}
                                    </span>
                                    <span className="response-score">
                                        {response.percentageScore.toFixed(1)}%
                                    </span>
                                </div>
                                <div className="response-details">
                                    <span>
                                        {response.correctAnswers}/{response.totalQuestions} correct
                                    </span>
                                    <span>
                                        Time: {Math.floor(response.timeSpent / 60)}m {response.timeSpent % 60}s
                                    </span>
                                    <span>
                                        {new Date(response.completedAt).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="action-buttons">
                {quiz.reviewStatus !== 'approved' && (
                    <button
                        className="btn-approve"
                        onClick={handleApproveQuiz}
                        disabled={actionLoading}
                    >
                        ✓ Approve Quiz
                    </button>
                )}
                {quiz.reviewStatus !== 'flagged' && (
                    <button
                        className="btn-flag"
                        onClick={() => setShowFlagDialog(true)}
                        disabled={actionLoading}
                    >
                        🚩 Flag Quiz
                    </button>
                )}
                <button
                    className="btn-delete"
                    onClick={handleDeleteQuiz}
                    disabled={actionLoading}
                >
                    🗑️ Delete Quiz
                </button>
            </div>

            {/* Flag Dialog */}
            {showFlagDialog && (
                <div className="modal-overlay" onClick={() => setShowFlagDialog(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Flag Quiz</h3>
                        <p>Please provide a reason for flagging this quiz:</p>
                        <textarea
                            value={flagReason}
                            onChange={(e) => setFlagReason(e.target.value)}
                            placeholder="Enter reason for flagging..."
                            rows={4}
                        />
                        <div className="modal-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => setShowFlagDialog(false)}
                                disabled={actionLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-submit"
                                onClick={handleFlagQuiz}
                                disabled={actionLoading || !flagReason.trim()}
                            >
                                {actionLoading ? 'Flagging...' : 'Flag Quiz'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
